<template>
    <div>
        <div v-if="article.visible">
            <div v-if="article.loading || loading_article_data" class="p-3">
                <content-placeholders>
                    <content-placeholders-text :lines="3" />
                </content-placeholders>
            </div>
            <div v-else>
                <div v-if="article_data">
                    <b-list-group v-show="hasAnyStudentElements" class="student-edition" flush>
                        <QuestionLayout
                            v-if="isElementVisible(element_visibility, 'article_activity_layout')"
                            :section-id="'unit' + unit.id + 'article' + article.id + 'activity_layout'"
                            :publication="publication"
                            :unit="unit"
                            :article="article"
                            :article-data="article_data"
                            :disabled="disabled"
                            :available-standards="available_standards"
                            :state-grade="state_grade"
                            :can-update-article="can_update_article"
                            :used-by-locked-publication="usedByLockedPublication"
                            header-title="Assessment Questions Layout"
                            add-button-label="Add Question"
                            @refresh-article="refreshQuestionLayout"
                        >
                            <template #header-actions>
                                <b-btn-group class="ml-2">
                                    <b-btn
                                        variant="light"
                                        @click="show_changelog_modal = true"
                                        content="Show Changelog"
                                        v-tippy="{placement: 'bottom'}"
                                    >
                                        <font-awesome-icon
                                            :icon="['fas', 'history']"
                                            fixed-width
                                        />
                                    </b-btn>
                                    <b-btn
                                        variant="light"
                                        :class="{
                                            'article-log-state': logUpdateType !== 'body_copied',
                                        }"
                                    >
                                        <font-awesome-icon :icon="['fad', 'save']" fixed-width />
                                    </b-btn>
                                    <CopyToClipboardButton
                                        class="p-2"
                                        :class="{
                                            'article-log-state': logUpdateType === 'body_copied',
                                        }"
                                        :value="async () => await questionsAsPlaintext()"
                                        :as-html="false"
                                        size="sm"
                                        @clipboard-copy="logUpdate"
                                    />
                                </b-btn-group>
                            </template>
                        </QuestionLayout>

                        <b-list-group-item
                            v-if="isElementVisible(element_visibility, 'article_assessment_pdf')"
                            class="rounded-0"
                            :id="'unit' + unit.id + 'article' + article.id + 'assessment_pdf'"
                        >
                            <div class="h6">Assessment PDF</div>
                            <div v-if="uploading_assessment">
                                <b-spinner small label="Loading..."></b-spinner>
                            </div>
                            <div v-else>
                                <div v-if="article_data.assessment_url">
                                    <b-list-group>
                                        <b-list-group-item>
                                            <div class="d-flex align-items-center">
                                                <div>
                                                    <b-link
                                                        :href="`${CDN_URL}${article_data.assessment_url}`"
                                                        target="_blank"
                                                        class="text-primary"
                                                    >
                                                        {{ assessmentLinkText }}
                                                    </b-link>
                                                </div>
                                                <div v-if="!disabled" class="ml-auto actions">
                                                    <b-link
                                                        href="#"
                                                        class="text-danger"
                                                        @click.prevent="removeAssessment"
                                                        v-if="can_update_article"
                                                    >
                                                        <font-awesome-icon :icon="['fad', 'trash']" />
                                                    </b-link>
                                                </div>
                                            </div>
                                        </b-list-group-item>
                                    </b-list-group>
                                </div>
                                <div v-else>
                                    <b-form-file
                                        v-model="assessment_file"
                                        accept="application/pdf"
                                        :disabled="!can_update_article || disabled"
                                    />
                                    <b-form-text>
                                        <span class="text-danger">No file has been uploaded</span>
                                    </b-form-text>
                                </div>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </div>
            </div>
        </div>
        <change-log-modal
            :visible="show_changelog_modal"
            :article-id="article.id"
            :unit-id="unit.id"
            :publication-id="publication.id"
            :title="article.title"
            :current-article="article_data"
            @update:visible="show_changelog_modal = $event"
        />
    </div>
</template>

<style lang="scss" scoped>
.student-edition {
    .list-group-item {
        border-left: 3px solid #B2EBF2;
    }
}

.card {
    &.collapsed {
        .card-header {
            border-radius: .25rem;
        }
    }

    .card-header {
        border-bottom: 0;
    }

    .list-group {
        .list-group-item {
            &:first-child {
                // border-radius: 0;
            }

            .list-group {
                .list-group-item {
                    // border-radius: inherit;
                }
            }
        }
    }
}

.article-log-state {
    background-color: rgb(52, 152, 219);
    color: white;
}

</style>

<script>
import {mapState} from 'vuex'
import { isElementVisible } from '@/common/elementVisibility'
import QuestionLayout from '@/components/article/QuestionLayout'
import moment from 'moment'
import eventBus from '@/common/event-bus'
import { CDN_URL } from '@/common/urlHelpers'
import CopyToClipboardButton from '@/components/CopyToClipboardButton'
import {exportQuestions} from '@/common/questionsExporter/exportQuestions'
import ChangeLogModal from '@/components/modals/article/ChangeLogModal'

export default {
    data: () => ({
        article_data: {},
        assessment_file: null,
        uploading_assessment: false,
        loading_article_data: false,

        show_changelog_modal: false,
    }),
    props: [
        'publication',
        'unit',
        'article',
        'initial_article_data',
        'disabled',
        'available_standards',
        'state_grade',
    ],
    components: {
        QuestionLayout,
        CopyToClipboardButton,
        ChangeLogModal,
    },
    computed: {
        hasAnyStudentElements() {
            const keys = [
                'article_activity_layout',
                'article_assessment_pdf',
            ]
            return keys.some(key => this.isElementVisible(key))
        },
        ...mapState({
            user: state => state.user.user,
            element_visibility: state => state.settings.element_visibility,
        }),
        can_update_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_article')
        },
        se_locked() {
            let se_locked

            if (
                this.publication.se_locked ||
                this.unit.se_locked
            ) {
                se_locked = true
            } else {
                se_locked = false
            }

            return se_locked
        },
        CDN_URL() {
            return CDN_URL
        },
        usedByLockedPublication() {
            const pedigrees = this.getLinkedAndLockedPedigrees()

            return pedigrees.length !== 0
        },
        logUpdateType() {
            if (this.article) {
                return this.$store.getters.latestLogTypeByArticleId(this.article.id)
            }

            return null
        },
        assessmentLinkText() {
            const articleTitle = this.article && this.article.title
                ? this.article.title
                : 'Assessment'

            return `${articleTitle} Assessment`
        }
    },
    watch: {
        'assessment_file': {
            handler(file) {
                if (file) {
                    this.uploadAssessment(file)
                }
            }
        },
        'article.visible': {
            handler(visible) {
                if (visible) {
                    this.getArticle()
                }
            }
        }
    },
    methods: {
        isElementVisible,
        getArticle() {
            this.loading_article_data = true

            return this.$store.dispatch('getArticles', this.article.article_id)
                .then(response => {
                    if (response.data) {
                        this.article_data = response.data[0]
                    }

                    this.loading_article_data = false
                    return response
                })
                .catch(error => {
                    if (
                        error &&
                        error.response &&
                        error.response.status &&
                        error.response.status == 401
                    ) {
                        this.$set(this.article, 'visible', false)
                    }

                    this.loading_article_data = false
                    throw error
                })
        },
        async refreshQuestionLayout() {
            await this.$store.dispatch(
                'getLatestLogType',
                {
                    article_id: this.article.id,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                }
            )

            await this.getArticle()
        },
        async logUpdate() {
            await this.$store.dispatch(
                'logArticleUpdate',
                {
                    article_id: this.article.id,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                    update_type: 'body_copied',
                }
            )
        },
        uploadAssessment(file) {
            const form_data = new FormData()

            if (!file) {
                return
            }

            form_data.append('assessment', file)
            this.uploading_assessment = true
            this.axios.post('/api/articles/add_assessment_pdf/' + this.article.article_id, form_data)
                .then(response => {
                    if (response.data && response.data.assessment_url) {
                        this.$set(this.article_data, 'assessment_url', response.data.assessment_url)
                    }

                    this.uploading_assessment = false
                    this.assessment_file = null
                })
                .catch(() => {
                    this.uploading_assessment = false
                    this.assessment_file = null
                })
        },
        removeAssessment() {
            let message = null

            if (this.article.title) {
                message = 'Are you sure you want to remove the Assessment PDF for <strong>' + this.article.title + '</strong>?'
            } else {
                message = 'Are you sure you want to remove this assessment PDF?'
            }

            this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Remove Assessment PDF',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/articles/remove_assessment_pdf/' + this.article.article_id)
                        .then(() => {
                            this.$set(this.article_data, 'assessment_url', null)
                        })
                        .catch(() => {

                        })
                }
            })
        },
        getLinkedAndLockedPedigrees() {
            const pedigrees = Array.isArray(this.article && this.article.pedigrees)
                ? this.article.pedigrees
                : []

            return pedigrees.filter((pedigree) => {
                const isDifferentUnit = pedigree.unit_id !== this.unit.id
                const isLinkedArticle = pedigree.relationship_type === 'Linked Article'

                return isDifferentUnit && isLinkedArticle && pedigree.locked
            })
        },
        async questionsAsPlaintext() {
            try {
                return exportQuestions(this.article_data.questions)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Could not copy questions as plaintext: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })

                return ''
            }
        },
    },
    mounted() {
        if (this.initial_article_data) {
            this.article_data = this.initial_article_data
        }

        if (this.article.id && this.unit.id && this.publication.id) {
            this.$store.dispatch(
                'getLatestLogType',
                {
                    article_id: this.article.id,
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                }
            )
        }
    },
    created() {
        eventBus.$emit('article-created')
        eventBus.$on('article-visible', (article_id) => {
            if (this.article.id == article_id) {
                if (!this.article.visible) {
                    this.$set(this.article, 'visible', true)
                }
            }
        })
    }
}
</script>
